import React from 'react';
import { useSelector } from 'react-redux';

import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { Modal } from 'components/ui/Modal/Modal';
import {
  getCurrentCurrency,
  getRecipient,
  getRecurringOffersMetadata,
} from 'selectors';
import { selectFirstOfferInstallments } from 'selectors/entities/recurringOffers/recurringOffers';
import { useTranslations } from 'utils';
import { formatToBrowserLocale } from 'utils/date/date';

export function PlanSummaryModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslations();
  const currency = useSelector(getCurrentCurrency);
  const recipient = useSelector(getRecipient);
  const installments = useSelector(selectFirstOfferInstallments);
  const { schedule } = useSelector(getRecurringOffersMetadata) || {};
  const today = formatToBrowserLocale(new Date().toString());

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <h2 className="text-2xl font-normal text-gray-950">
        {t('planSummaryModal.title')}
      </h2>
      <div className="overflow-auto rounded-t-lg border border-b-0 border-gray-300 bg-white outline outline-white/5">
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="p-4">
              <th className="border-b border-gray-300 p-4 pb-3 text-sm font-semibold uppercase text-gray-950">
                #
              </th>
              <th className="border-b border-gray-300 p-4 pb-3 text-sm font-semibold uppercase text-gray-950">
                {t('planSummaryModal.columns.chargeDate')}
              </th>
              <th className="border-b border-gray-300 p-4 pb-3 text-sm font-semibold uppercase text-gray-950">
                {t('planSummaryModal.columns.installmentAmount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {installments?.map((installment, index) => {
              const installmentDate = formatToBrowserLocale(installment.date);

              return (
                <tr key={index}>
                  <td className="border-b border-gray-300 p-4 text-sm">
                    {index + 1}
                  </td>
                  <td className="border-b border-gray-300 p-4 text-sm">
                    {installmentDate === today ||
                    (index === 0 && schedule === 'pay_now')
                      ? t('planSummaryModal.today')
                      : installmentDate}
                  </td>
                  <td className="border-b border-gray-300 p-4 text-sm">
                    <FormattedAmount
                      value={installment.amount}
                      currency={currency.code}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mb-0 mt-6 text-xs/6 text-gray-700 ">
        <span className="font-medium text-gray-950">
          {t('planSummaryModal.note.prefix')}
        </span>{' '}
        {t('planSummaryModal.note.message', {
          recipientName: `${recipient.name}`,
        })}
      </p>
    </Modal>
  );
}
